import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  Modal,
  Fade,
  Grid,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  Paper,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Call as CallIcon,
  Assignment as FormIcon,
  People as PeopleIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Autorenew as RenewIcon,
} from '@mui/icons-material';
import {
  getBeneficiaries,
  sendNotification,
  scheduleCall,
  getUserPlan,
} from '../services/api_service';
import { useAlert } from '../AlertContext';
import { useNavigate } from 'react-router-dom';

const ModernBeneficiaries = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [plan, setPlan] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({ contact: '', name: '' });
  const [submitting, setSubmitting] = useState(false);

  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const session = JSON.parse(localStorage.getItem('session') || '{}');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [beneficiariesResponse, planResponse] = await Promise.all([
        getBeneficiaries(session.user_id),
        getUserPlan(session.user_id),
      ]);

      if (beneficiariesResponse?.result) {
        setUsers(beneficiariesResponse.result);
      }
      if (planResponse?.result) {
        setPlan(planResponse.result[0] || {});
      }
    } catch (error) {
      showAlert('Error al cargar datos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setModalOpen(false);
    setCurrentStep(0);
    setFormData({ contact: '', name: '' });
  };

  const sendInvitation = async (type) => {
    if (!formData.contact) {
      showAlert('Por favor ingresa el contacto', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const config = {
        channel: type === 'whatsapp' ? 'WPP' : 'EMAIL',
        contact: formData.contact,
        type: 'invitacion_beneficiario',
        username: session.user_name,
        link:
          type === 'whatsapp'
            ? // eslint-disable-next-line no-undef
              `${process.env.REACT_APP_PUBLIC_HOST}/registration/${session.user_id}`
            : // eslint-disable-next-line no-undef
              `${process.env.REACT_APP_PUBLIC_HOST}/register?idx=${session.identification_number}&redirect_to=login`,
      };

      await sendNotification(config);
      showAlert('Invitación enviada exitosamente', 'success');
      resetModal();
    } catch (error) {
      showAlert('Error al enviar invitación', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const requestCall = async () => {
    if (!formData.name || !formData.contact) {
      showAlert('Por favor completa todos los campos', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        name: formData.name,
        phone: formData.contact,
        user_name: session.user_name,
        user_document: session.identification_number,
        user_id: session.user_id,
      };

      await scheduleCall(body);
      showAlert('Solicitud de contacto enviada exitosamente', 'success');
      resetModal();
    } catch (error) {
      showAlert('Error al solicitar contacto', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    return names.length >= 2
      ? `${names[0][0]}${names[1][0]}`.toUpperCase()
      : names[0][0].toUpperCase();
  };

  const stringToColor = (string) => {
    if (!string) return '#94B754';
    let hash = 0;
    for (let i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
  };

  const addMethods = [
    {
      id: 'whatsapp',
      title: 'WhatsApp',
      description: 'Enviar invitación por WhatsApp',
      icon: <WhatsAppIcon sx={{ fontSize: 40, color: '#25D366' }} />,
      color: '#25D366',
    },
    {
      id: 'email',
      title: 'Correo Electrónico',
      description: 'Enviar invitación por email',
      icon: <EmailIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      color: '#1976d2',
    },
    {
      id: 'form',
      title: 'Formulario Directo',
      description: 'Llenar formulario ahora',
      icon: <FormIcon sx={{ fontSize: 40, color: '#ff9800' }} />,
      color: '#ff9800',
    },
    {
      id: 'call',
      title: 'Contacto CLODAN',
      description: 'Que CLODAN contacte al beneficiario',
      icon: <CallIcon sx={{ fontSize: 40, color: '#4caf50' }} />,
      color: '#4caf50',
    },
  ];

  const renderModalContent = () => {
    if (currentStep === 0) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, textAlign: 'center' }}>
            ¿Cómo quieres añadir a tu beneficiario?
          </Typography>
          <Grid container spacing={2}>
            {addMethods.map((method) => (
              <Grid item xs={12} key={method.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: '1px solid #E0E0E0',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      borderColor: method.color,
                    },
                  }}
                  onClick={() => {
                    if (method.id === 'form') {
                      const url = new URL(window.location.origin + '/register');
                      url.searchParams.set(
                        'idx',
                        session.identification_number
                      );
                      url.searchParams.set('redirect_to', 'beneficiaries');
                      window.location.href = url;
                    } else {
                      setCurrentStep(method.id);
                    }
                  }}
                >
                  <CardContent
                    sx={{ display: 'flex', alignItems: 'center', p: 2 }}
                  >
                    {method.icon}
                    <Box sx={{ ml: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {method.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {method.description}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      );
    }

    const method = addMethods.find((m) => m.id === currentStep);

    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => setCurrentStep(0)} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          {method?.icon}
          <Typography variant="h6" sx={{ ml: 1 }}>
            {method?.title}
          </Typography>
        </Box>

        {currentStep === 'whatsapp' && (
          <>
            <TextField
              label="Número de WhatsApp"
              fullWidth
              value={formData.contact}
              onChange={(e) =>
                setFormData({ ...formData, contact: e.target.value })
              }
              placeholder="+57 300 123 4567"
              sx={{ mb: 3 }}
            />
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={() => sendInvitation('whatsapp')}
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <WhatsAppIcon />
                )
              }
              sx={{
                backgroundColor: '#25D366',
                '&:hover': { backgroundColor: '#1DA851' },
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
              }}
            >
              {submitting ? 'Enviando...' : 'Enviar Invitación'}
            </Button>
          </>
        )}

        {currentStep === 'email' && (
          <>
            <TextField
              label="Correo Electrónico"
              fullWidth
              type="email"
              value={formData.contact}
              onChange={(e) =>
                setFormData({ ...formData, contact: e.target.value })
              }
              placeholder="ejemplo@correo.com"
              sx={{ mb: 3 }}
            />
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={() => sendInvitation('email')}
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <EmailIcon />
                )
              }
              sx={{
                backgroundColor: '#1976d2',
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
              }}
            >
              {submitting ? 'Enviando...' : 'Enviar Invitación'}
            </Button>
          </>
        )}

        {currentStep === 'call' && (
          <>
            <TextField
              label="Nombre Completo"
              fullWidth
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              sx={{ mb: 2 }}
            />
            <TextField
              label="Número de Teléfono"
              fullWidth
              value={formData.contact}
              onChange={(e) =>
                setFormData({ ...formData, contact: e.target.value })
              }
              placeholder="+57 300 123 4567"
              sx={{ mb: 3 }}
            />
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={requestCall}
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CallIcon />
                )
              }
              sx={{
                backgroundColor: '#4caf50',
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
              }}
            >
              {submitting ? 'Enviando...' : 'Solicitar Contacto'}
            </Button>
          </>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#94B754' }} />
      </Box>
    );
  }

  const isActive =
    plan.description?.toUpperCase() !== 'NO AFILIADO' && session.plan_id;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #F8FFFE 0%, #FFFFFF 100%)',
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="md">
        <Fade in={true} timeout={600}>
          <Box>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant={isMobile ? 'h4' : 'h3'}
                sx={{
                  fontWeight: 'bold',
                  color: '#2C5530',
                  mb: 1,
                }}
              >
                Beneficiarios
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Gestiona los beneficiarios de tu plan de salud
              </Typography>
            </Box>

            {isActive ? (
              <>
                {/* Beneficiaries List */}
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    border: '1px solid #E0E0E0',
                    mb: 3,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      background:
                        'linear-gradient(135deg, #94B754 0%, #7DA647 100%)',
                      color: 'white',
                      p: 3,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PeopleIcon sx={{ mr: 1, fontSize: 28 }} />
                      <Typography variant="h6" fontWeight="bold">
                        Mis Beneficiarios
                      </Typography>
                    </Box>
                    <Chip
                      label={`${users.length}/3`}
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  </Box>

                  <CardContent sx={{ p: 0 }}>
                    {users.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <PeopleIcon
                          sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }}
                        />
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          No tienes beneficiarios inscritos
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Añade beneficiarios para compartir tu plan
                        </Typography>
                      </Box>
                    ) : (
                      <List sx={{ p: 0 }}>
                        {users.map((user, index) => (
                          <ListItem
                            key={index}
                            sx={{
                              py: 2,
                              px: 3,
                              borderBottom:
                                index < users.length - 1
                                  ? '1px solid #E0E0E0'
                                  : 'none',
                            }}
                          >
                            <ListItemAvatar>
                              <Avatar
                                sx={{
                                  backgroundColor: stringToColor(user.name),
                                  width: 56,
                                  height: 56,
                                  fontSize: '1.2rem',
                                  fontWeight: 'bold',
                                }}
                              >
                                {getInitials(user.name)}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography variant="h6" fontWeight="bold">
                                  {user.name}
                                </Typography>
                              }
                              secondary={
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {user.identification_type}{' '}
                                  {user.identification_number}
                                </Typography>
                              }
                              sx={{ ml: 2 }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </CardContent>
                </Paper>

                {/* Add Beneficiary Button */}
                {users.length < 3 && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => setModalOpen(true)}
                      startIcon={<PersonAddIcon />}
                      sx={{
                        background:
                          'linear-gradient(135deg, #94B754 0%, #7DA647 100%)',
                        py: 1.5,
                        px: 4,
                        borderRadius: 3,
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        textTransform: 'none',
                        boxShadow: '0 4px 12px rgba(148, 183, 84, 0.4)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 16px rgba(148, 183, 84, 0.5)',
                        },
                      }}
                    >
                      Añadir Beneficiario
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              /* Inactive Plan */
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: '1px solid #E0E0E0',
                  textAlign: 'center',
                  p: 6,
                }}
              >
                <PeopleIcon
                  sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }}
                />
                <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                  Plan No Activo
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 4 }}
                >
                  Necesitas renovar tu plan para poder añadir beneficiarios
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/pay')}
                  startIcon={<RenewIcon />}
                  sx={{
                    background:
                      'linear-gradient(135deg, #94B754 0%, #7DA647 100%)',
                    py: 1.5,
                    px: 4,
                    borderRadius: 3,
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    textTransform: 'none',
                    boxShadow: '0 4px 12px rgba(148, 183, 84, 0.4)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 16px rgba(148, 183, 84, 0.5)',
                    },
                  }}
                >
                  Renovar Plan
                </Button>
              </Paper>
            )}
          </Box>
        </Fade>

        {/* Add Beneficiary Modal */}
        <Modal
          open={modalOpen}
          onClose={resetModal}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Fade in={modalOpen}>
            <Paper
              sx={{
                minWidth: { xs: '90%', sm: 400 },
                maxWidth: 500,
                maxHeight: '90vh',
                overflow: 'auto',
                borderRadius: 4,
                boxShadow: 24,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 3,
                  borderBottom: '1px solid #E0E0E0',
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  Añadir Beneficiario
                </Typography>
                <IconButton onClick={resetModal}>
                  <CloseIcon />
                </IconButton>
              </Box>
              {renderModalContent()}
            </Paper>
          </Fade>
        </Modal>
      </Container>
    </Box>
  );
};

export default ModernBeneficiaries;
